import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    API_ADD_CLIENT,
    API_BRANCHES,
    API_CENTERS,
    API_CLIENT_DETAIL,
    API_UPDATE_CLIENT,
    API_VIEW_CLIENT_DOCUMENT,
} from '../Config/ApiUrlConstant';
import SuccessModal from '../Common/SuccessModal';
import DocumentLinks from '../Common/DocumentLinks';
import { apiJson, apiRequest } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';

const initialFormData = {
    id: '',
    branch_id: '',
    center_id: '',
    client_name: '',
    client_dob: '',
    contact_no: '',
    client_uid: '',
    client_pan: '',
    client_father_name: '',
    client_mother_name: '',
    client_husband_name: '',
    client_husband_dob: '',
    husband_father_name: '',
    husband_mother_name: '',
    client_address: '',
    client_business: '',
    husband_address: '',
    purpose_of_loan: '',
    client_uid_upload: null,
    client_pan_upload_path: null,
    client_ration_card_upload_path: null,
};

const normalizeDateInputValue = (value) => {
    if (!value) {
        return '';
    }

    const stringValue = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
        return stringValue;
    }

    const parsedDate = new Date(stringValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return parsedDate.toISOString().slice(0, 10);
};

const toInputValue = (value) => value === null || value === undefined ? '' : String(value);

const ClientDetailsForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { clientId: routeClientId } = useParams();
    const clientId = routeClientId || location.state?.clientId || '';
    const [formData, setFormData] = useState(initialFormData);
    const [message, setMessage] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [centerList, setCenterList] = useState([]);
    const [branches, setBranches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [documentAvailability, setDocumentAvailability] = useState({});

    const loadBranches = async () => {
        const { data } = await apiJson(API_BRANCHES, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        setBranches(data?.data || []);
    };

    const getCentersByBranch = async (branchId) => {
        const { data } = await apiJson(API_CENTERS, {
            method: 'POST',
            body: JSON.stringify({ is_all: false, branch_id: branchId }),
        });

        setCenterList(data?.data || []);
    };

    useEffect(() => {
        const loadFormData = async () => {
            try {
                setIsLoading(true);
                await loadBranches();

                if (!clientId) {
                    return;
                }

                const { data } = await apiJson(API_CLIENT_DETAIL, {
                    method: 'POST',
                    body: JSON.stringify({ client_id: clientId }),
                });

                const client = data?.data;
                if (!client) {
                    return;
                }

                await getCentersByBranch(client.branch_id);
                setDocumentAvailability({
                    uid: Boolean(client.has_client_uid_upload),
                    pan: Boolean(client.has_client_pan_upload),
                    ration: Boolean(client.has_client_ration_card_upload),
                });
                setFormData({
                    id: toInputValue(client.id),
                    branch_id: toInputValue(client.branch_id),
                    center_id: toInputValue(client.center_id),
                    client_name: toInputValue(client.client_name),
                    client_dob: normalizeDateInputValue(client.client_dob),
                    contact_no: toInputValue(client.contact_no),
                    client_uid: toInputValue(client.client_uid),
                    client_pan: toInputValue(client.client_pan),
                    client_father_name: toInputValue(client.client_father_name),
                    client_mother_name: toInputValue(client.client_mother_name),
                    client_husband_name: toInputValue(client.client_husband_name),
                    client_husband_dob: normalizeDateInputValue(client.client_husband_dob),
                    husband_father_name: toInputValue(client.husband_father_name),
                    husband_mother_name: toInputValue(client.husband_mother_name),
                    client_address: toInputValue(client.client_address),
                    client_business: toInputValue(client.client_business),
                    husband_address: toInputValue(client.husband_address),
                    purpose_of_loan: toInputValue(client.purpose_of_loan),
                    client_uid_upload: null,
                    client_pan_upload_path: null,
                    client_ration_card_upload_path: null,
                });
            } catch (error) {
                setMessage('Unable to load client details.');
            } finally {
                setIsLoading(false);
            }
        };

        void loadFormData();
    }, [clientId]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'branch_id') {
            if (value) {
                void getCentersByBranch(value);
                setFormData((prevData) => ({
                    ...prevData,
                    branch_id: value,
                    center_id: '',
                }));
                return;
            }

            setCenterList([]);
            setFormData((prevData) => ({
                ...prevData,
                branch_id: '',
                center_id: '',
            }));
            return;
        }

        if (files?.length) {
            setFormData((prevData) => ({
                ...prevData,
                [name]: files[0],
            }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!confirmAction(clientId ? 'update this client' : 'add this client')) return;

        const formDataObj = new FormData();

        for (const [key, value] of Object.entries(formData)) {
            if (value && key !== 'client_uid_upload' && key !== 'client_pan_upload_path' && key !== 'client_ration_card_upload_path') {
                formDataObj.append(key, value);
            }
        }

        if (formData.client_uid_upload instanceof File) {
            formDataObj.append('client_uid_upload', formData.client_uid_upload);
        }
        if (formData.client_pan_upload_path instanceof File) {
            formDataObj.append('client_pan_upload_path', formData.client_pan_upload_path);
        }
        if (formData.client_ration_card_upload_path instanceof File) {
            formDataObj.append('client_ration_card_upload_path', formData.client_ration_card_upload_path);
        }

        try {
            const response = await apiRequest(clientId ? API_UPDATE_CLIENT : API_ADD_CLIENT, {
                method: 'POST',
                body: formDataObj,
                isFormData: true,
            });

            const responseData = await response.json();

            if (responseData.status) {
                setMessage(responseData.message);
                setModalVisible(true);

                if (!clientId) {
                    setFormData(initialFormData);
                    setCenterList([]);
                }

                setTimeout(() => {
                    setModalVisible(false);
                    navigate('/clients');
                }, 1500);
            } else {
                setMessage(responseData.message || 'Unable to save client');
                setModalVisible(true);
            }
        } catch (error) {
            setMessage('Error saving client');
            setModalVisible(true);
        }
    };

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>{clientId ? 'Edit Client' : 'Client Details Form'}</h2>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/clients')}>
                    Back to Clients
                </button>
            </div>
            {message && clientId ? <div className="alert alert-info">{message}</div> : null}
            {isLoading ? <div className="dashboard-empty-state">Loading client details...</div> : null}
            <form onSubmit={handleSubmit}>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label>Branch Name *</label>
                        <select className="form-control" name="branch_id" value={formData.branch_id} onChange={handleChange} required>
                            <option value="">Select Branch</option>
                            {branches.map((branch) => (
                                <option key={branch.id} value={branch.id}>{branch.branch_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label>Center Name *</label>
                        <select className="form-control" name="center_id" value={formData.center_id} onChange={handleChange} required>
                            <option value="">Select Center</option>
                            {centerList.map((center) => (
                                <option key={center.id} value={center.id}>{center.center_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label>Client Name *</label>
                        <input type="text" className="form-control" name="client_name" value={formData.client_name} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                        <label>Client DOB *</label>
                        <input type="date" className="form-control" name="client_dob" value={normalizeDateInputValue(formData.client_dob)} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                        <label>Contact Number *</label>
                        <input type="text" className="form-control" name="contact_no" value={formData.contact_no} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                        <label>Client UID</label>
                        <input type="text" className="form-control" name="client_uid" value={formData.client_uid} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Client PAN</label>
                        <input type="text" className="form-control" name="client_pan" value={formData.client_pan} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Client Business *</label>
                        <input type="text" className="form-control" name="client_business" value={formData.client_business} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                        <label>Father Name</label>
                        <input type="text" className="form-control" name="client_father_name" value={formData.client_father_name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Mother Name</label>
                        <input type="text" className="form-control" name="client_mother_name" value={formData.client_mother_name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Husband Name</label>
                        <input type="text" className="form-control" name="client_husband_name" value={formData.client_husband_name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Husband DOB</label>
                        <input type="date" className="form-control" name="client_husband_dob" value={normalizeDateInputValue(formData.client_husband_dob)} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Husband Father Name</label>
                        <input type="text" className="form-control" name="husband_father_name" value={formData.husband_father_name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label>Husband Mother Name</label>
                        <input type="text" className="form-control" name="husband_mother_name" value={formData.husband_mother_name} onChange={handleChange} />
                    </div>
                    <div className="col-12">
                        <label>Client Address *</label>
                        <textarea className="form-control" name="client_address" value={formData.client_address} onChange={handleChange} required />
                    </div>
                    <div className="col-12">
                        <label>Husband Address</label>
                        <textarea className="form-control" name="husband_address" value={formData.husband_address} onChange={handleChange} />
                    </div>
                    <div className="col-12">
                        <label>Purpose of Loan *</label>
                        <input type="text" className="form-control" name="purpose_of_loan" value={formData.purpose_of_loan} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4">
                        <label>UID Upload</label>
                        <input type="file" className="form-control" name="client_uid_upload" accept=".pdf,.jpg,.jpeg,.png" onChange={handleChange} />
                        <small className="text-muted">PDF, JPG, or PNG up to 5 MB</small>
                    </div>
                    <div className="col-md-4">
                        <label>PAN Upload</label>
                        <input type="file" className="form-control" name="client_pan_upload_path" accept=".pdf,.jpg,.jpeg,.png" onChange={handleChange} />
                        <small className="text-muted">PDF, JPG, or PNG up to 5 MB</small>
                    </div>
                    <div className="col-md-4">
                        <label>Ration Card Upload</label>
                        <input type="file" className="form-control" name="client_ration_card_upload_path" accept=".pdf,.jpg,.jpeg,.png" onChange={handleChange} />
                        <small className="text-muted">PDF, JPG, or PNG up to 5 MB</small>
                    </div>
                    {clientId ? (
                        <div className="col-12">
                            <label>Uploaded Documents</label>
                            <DocumentLinks
                                endpoint={API_VIEW_CLIENT_DOCUMENT}
                                ownerType="client"
                                ownerId={clientId}
                                documents={[
                                    { field: 'client_uid_upload', label: 'View UID', available: documentAvailability.uid },
                                    { field: 'client_pan_upload_path', label: 'View PAN', available: documentAvailability.pan },
                                    { field: 'client_ration_card_upload_path', label: 'View Ration Card', available: documentAvailability.ration },
                                ]}
                            />
                        </div>
                    ) : null}
                </div>
                <div className="mt-4 d-flex gap-2">
                    <button type="submit" className="btn btn-primary">{clientId ? 'Update Client' : 'Submit'}</button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setFormData(initialFormData)}>Reset</button>
                </div>
            </form>
            {modalVisible && (
                <SuccessModal message={message} onClose={() => setModalVisible(false)} />
            )}
        </div>
    );
};

export default ClientDetailsForm;
